/**
 * Security Risk Monitor Component
 * Displays security alerts and risk analysis
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Shield, AlertTriangle, AlertOctagon, Info,
  RefreshCw, User, Download, Eye, Clock
} from 'lucide-react';
import agenticApi from '../../api/agenticApi';

const RiskMonitor = ({ headers }) => {
  const [analysis, setAnalysis] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [analysisRes, alertsRes] = await Promise.all([
        agenticApi.getRiskAnalysis(24, headers),
        agenticApi.getSecurityAlerts(headers)
      ]);
      
      setAnalysis(analysisRes.data);
      setAlerts(alertsRes.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch security data:', err);
      setError(err.response?.data?.detail || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon className="w-4 h-4 text-red-600" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default:
        return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || colors.low;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      critical: 'text-red-600',
      high: 'text-orange-600',
      medium: 'text-yellow-600',
      low: 'text-green-600',
      none: 'text-slate-600'
    };
    return colors[level] || colors.none;
  };

  const getAnomalyIcon = (type) => {
    if (type.includes('download')) return <Download className="w-4 h-4" />;
    if (type.includes('access')) return <Eye className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  if (loading && !analysis) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-3" />
          <p className="text-slate-600">Analyzing security data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6 text-center">
          <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <p className="text-red-700">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={fetchData}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="risk-monitor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-slate-700" />
          <h2 className="text-xl font-semibold text-slate-800">Security Risk Monitor</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Risk Summary */}
      {analysis && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={analysis.risk_level === 'critical' ? 'border-red-300 bg-red-50' : 
                          analysis.risk_level === 'high' ? 'border-orange-300 bg-orange-50' : ''}>
            <CardContent className="pt-4">
              <div className="text-xs text-slate-500 mb-1">Risk Level</div>
              <div className={`text-2xl font-bold ${getRiskLevelColor(analysis.risk_level)}`}>
                {analysis.risk_level?.toUpperCase()}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-slate-500 mb-1">Risk Score</div>
              <div className="text-2xl font-bold text-slate-800">
                {analysis.risk_score}/100
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-xs text-slate-500 mb-1">Activities (24h)</div>
              <div className="text-2xl font-bold text-slate-800">
                {analysis.total_activities}
              </div>
            </CardContent>
          </Card>
          
          <Card className={analysis.anomalies?.length > 0 ? 'border-amber-300 bg-amber-50' : ''}>
            <CardContent className="pt-4">
              <div className="text-xs text-slate-500 mb-1">Anomalies</div>
              <div className={`text-2xl font-bold ${analysis.anomalies?.length > 0 ? 'text-amber-600' : 'text-slate-800'}`}>
                {analysis.anomalies?.length || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-800">
              <AlertOctagon className="w-4 h-4" />
              Active Security Alerts
              <Badge className="bg-red-100 text-red-800 ml-auto">{alerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((alert, i) => (
              <div 
                key={i}
                className={`p-3 rounded-lg border ${
                  alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                  alert.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(alert.severity)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm">{alert.title}</h4>
                      <Badge className={getSeverityBadge(alert.severity)} variant="outline">
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{alert.message}</p>
                    {alert.metadata?.user_id && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                        <User className="w-3 h-3" />
                        {alert.metadata.user_id}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Anomalies Detail */}
      {analysis?.anomalies?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Detected Anomalies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analysis.anomalies.map((anomaly, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  <div className="p-2 bg-white rounded">
                    {getAnomalyIcon(anomaly.type)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{anomaly.type.replace(/_/g, ' ')}</div>
                    <div className="text-xs text-slate-500">{anomaly.description}</div>
                  </div>
                  <Badge className={getSeverityBadge(anomaly.severity)}>
                    {anomaly.severity}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {analysis?.recommendations?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Security Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {analysis.recommendations.map((rec, i) => (
                <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-purple-600">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* AI Analysis */}
      {analysis?.ai_analysis && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-600" />
              AI Security Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{analysis.ai_analysis}</p>
          </CardContent>
        </Card>
      )}

      {/* No Issues */}
      {(!analysis?.anomalies?.length && !alerts.length) && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-6 text-center">
            <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="font-medium text-green-800">All Clear</h3>
            <p className="text-sm text-green-700 mt-1">No security anomalies detected in the last 24 hours.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RiskMonitor;
